import React, { useState, useCallback } from 'react'

import backgroundImage from '../images/header-17.jpg'
import SocialLoginButtons from '../components/SocialLoginButtons'
import { Link } from 'react-router-dom'
import { Auth } from 'aws-amplify'
import useTitle from '../hooks/useTitle'

interface StateData {
  signInProgressStep: "INITIAL" | "REFRESH_PASSWORD",
  errorMessage: boolean | string,
  inflightUser: null,
}

const initialState: StateData = {
  signInProgressStep: "INITIAL",
  errorMessage: false,
  inflightUser: null,
}

interface LoginFormElements extends HTMLFormControlsCollection {
  email: HTMLInputElement
  password: HTMLInputElement
}

const Login: React.FC = () => {
  useTitle("Login | Speech2")

  const [ state, setState ] = useState<StateData>(initialState)

  const login = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setState(s => ({...s, errorMessage: false}))

      const elements = event.currentTarget.elements as LoginFormElements
      const email = elements.email.value
      const password = elements.password.value

      try {
        const user = await Auth.signIn(email, password)
        if (user.challengeName === 'SMS_MFA' || user.challengeName === 'SOFTWARE_TOKEN_MFA') {
          console.log("User requires MFA", user)
        } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          console.log("New password required", user)
          setState(s => ({
            ...s,
            signInProgressStep: 'REFRESH_PASSWORD',
            inflightUser: user
          }))
        } else if (user.challengeName === 'MFA_SETUP') {
          console.log("MFA_SETUP not implemented yet.", user)
        } else {
          // The user directly signs in.
          // This event is caught by the session context, so we don't
          // need to do anything else here.
        }
      } catch (err) {
        if (err.code === 'UserNotConfirmedException') {
          // The error happens if the user didn't finish the confirmation step when signing up
          // In this case you need to resend the code and confirm the user
          // About how to resend the code and confirm the user, please check the signUp part
          console.log("UserNotConfirmedException", err)
        } else if (err.code === 'PasswordResetRequiredException') {
          // The error happens when the password is reset in the Cognito console
          // In this case you need to call forgotPassword to reset the password
          // Please check the Forgot Password part.
          console.log("PasswordResetRequiredException", err)
        } else if (err.code === 'UserNotFoundException' || err.code === 'NotAuthorizedException') {
          setState(s => ({
            ...s,
            errorMessage: "The email and/or password you provided is invalid. Please try again."
          }))
          elements.password.value = ""
        } else {
          console.log("Cognito error", err)
          throw err
        }
      }
    }, [ setState ]
  )

  const loginScreen = state.signInProgressStep === 'INITIAL' && (
    <div className="col-11 col-md-8 col-lg-10 col-xl-9 py-4">
        <h1 className="h2 text-center">Welcome back</h1>
        {state.errorMessage && <div className="alert alert-danger" role="alert">{state.errorMessage}</div>}
        <SocialLoginButtons />

        <form onSubmit={login}>
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              tabIndex={1}
              id="email"
              type="email"
              name="email"
              className="form-control"
              placeholder="Enter your email address"
              required={true}
            />
          </div>
          <div className="form-group">
            <div className="d-flex justify-content-between">
              <label htmlFor="password">Password:</label>
              <small>
                <Link to="/forgot-password">Forgot password?</Link>
              </small>
            </div>
            <input
              tabIndex={2}
              id="password"
              type="password"
              name="password"
              className="form-control"
              placeholder="Choose a password"
              required={true}
            />
          </div>
          <div className="form-group">
            <button tabIndex={3} className="btn btn-block btn-lg btn-primary" type="submit">Log in</button>
          </div>
        </form>
        <div className="text-center text-small mt-3">
          <span>Don't have an account? <Link to="/signup">Sign up here</Link></span>
        </div>
      </div>
  )

  const passwordRefreshScreen = null

  return (
    <section className="row no-gutters p-0 height-100 align-items-initial">
      <div className="col-lg d-lg-flex order-lg-2 height-30">
        <img src={backgroundImage} alt="background" className="bg-image" />
      </div>
      <div className="col-lg-5 p-0 d-flex bg-white align-items-lg-center">
        <div className="row no-gutters flex-fill justify-content-center">
          { loginScreen }
          { passwordRefreshScreen }
        </div>
      </div>
    </section>
  )
}

export default Login
